/**
 * Context sync service — pulls server data based on navigation context.
 *
 * List sync: fetch all entities of a type the user can access,
 * compare versions with local Dexie, update if server is newer.
 *
 * Children sync: fetch children of a specific entity (layer groups,
 * features) and apply the same version comparison.
 *
 * Revocation: entities that exist locally but are no longer returned
 * by the server are cleaned up (deleted locally with cascade).
 */

import { db } from '../database/schema.js'
import type { ContextSyncApi, EntitySummary } from './context-sync-api.js'
import { applyServerChange } from './server-apply.js'
import { featureStorage } from './feature-storage.js'
import { edgeService, nodeRef, parseNodeRef } from '../services/edge-service.js'

async function getPendingDeleteIds(): Promise<Set<string>> {
  const pending = await db.syncEvents.filter(e => !e.syncedAt && e.operation === 'delete').toArray()
  return new Set(pending.map(e => `${e.entityType}:${e.entityId}`))
}

const ENTITY_TABLES: Record<string, string> = {
  route: 'routes',
  routes: 'routes',
  plan: 'plans',
  plans: 'plans',
  activity: 'activities',
  activities: 'activities',
  feature: 'features',
  features: 'features',
  layerGroup: 'layerGroups',
  layerGroups: 'layerGroups',
  edge: 'edges',
  edges: 'edges',
  team: 'teams',
  teams: 'teams',
  teamMember: 'teamMembers',
  teamMembers: 'teamMembers',
  user: 'users',
  users: 'users',
  fieldLogEntry: 'fieldLogEntries',
  fieldLogEntries: 'fieldLogEntries',
}

export interface SyncListResult {
  success: boolean
  updated: string[]
  added: string[]
  revoked: string[]
  error?: string
}

export interface SyncChildrenResult {
  success: boolean
  updated: string[]
  added: string[]
  removed: string[]
  error?: string
}

export class ContextSyncService {
  constructor(private api: ContextSyncApi) {}

  async syncList(entityType: string): Promise<SyncListResult> {
    const result: SyncListResult = { success: true, updated: [], added: [], revoked: [] }

    try {
      const response = await this.api.fetchList(entityType)
      const serverIds = new Set<string>()
      const pendingDeletes = await getPendingDeleteIds()

      await applyServerChange(async () => {
        for (const entity of response.entities) {
          serverIds.add(entity.entityId)
          if (pendingDeletes.has(`${entity.entityType}:${entity.entityId}`)) continue
          const action = await this.applyEntity(entity)
          if (action === 'added') result.added.push(entity.entityId)
          else if (action === 'updated') result.updated.push(entity.entityId)
        }
      })

      const tableName = ENTITY_TABLES[entityType]
      if (tableName) {
        const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]
        const allLocal = await table.toArray()
        for (const local of allLocal) {
          const rec = local as Record<string, unknown>
          const id = rec.id as string
          const version = (rec.version as number | undefined) ?? 0
          const accessStatus = rec.accessStatus as string | undefined

          if (version > 0 && accessStatus !== 'revoked' && !serverIds.has(id)) {
            if (pendingDeletes.has(`${entityType}:${id}`)) continue
            await applyServerChange(() => this.revokeEntity(entityType, id))
            result.revoked.push(id)
          }
        }
      }
    } catch (err) {
      result.success = false
      result.error = err instanceof Error ? err.message : String(err)
    }

    return result
  }

  async syncChildren(parentType: string, parentId: string): Promise<SyncChildrenResult> {
    const result: SyncChildrenResult = { success: true, updated: [], added: [], removed: [] }

    try {
      const response = await this.api.fetchChildren(parentType, parentId)
      const pendingDeletes = await getPendingDeleteIds()

      const serverIdsByType = new Map<string, Set<string>>()

      await applyServerChange(async () => {
        for (const entity of response.entities) {
          const typeSet = serverIdsByType.get(entity.entityType) ?? new Set()
          typeSet.add(entity.entityId)
          serverIdsByType.set(entity.entityType, typeSet)

          if (pendingDeletes.has(`${entity.entityType}:${entity.entityId}`)) continue
          const action = await this.applyEntity(entity)
          if (action === 'added') result.added.push(entity.entityId)
          else if (action === 'updated') result.updated.push(entity.entityId)
        }
      })

      const removed = await applyServerChange(() =>
        this.removeOrphanedChildren(parentType, parentId, serverIdsByType, pendingDeletes)
      )
      result.removed = removed
    } catch (err) {
      result.success = false
      result.error = err instanceof Error ? err.message : String(err)
    }

    return result
  }

  /**
   * Compare local children against the server response and remove
   * any that the server no longer returns. Works generically across
   * all entity and edge types.
   */
  private async removeOrphanedChildren(
    parentType: string,
    parentId: string,
    serverIdsByType: Map<string, Set<string>>,
    pendingDeletes: Set<string>
  ): Promise<string[]> {
    const removed: string[] = []
    const serverEdgeIds = serverIdsByType.get('edge') ?? new Set()

    const parentRef = nodeRef(parentType, parentId)

    // Collect all local edge scopes: parent + its layer groups
    const subjectRefs = [parentRef]
    const groupIds = await edgeService.getObjectIds(parentRef, 'has-layer-group')
    for (const gid of groupIds) {
      subjectRefs.push(nodeRef('group', gid))
    }

    // Walk local edges, remove orphaned edges and their target entities
    for (const ref of subjectRefs) {
      const localEdges = await edgeService.getAllFromSubject(ref)
      for (const edge of localEdges) {
        if (serverEdgeIds.has(edge.id)) continue
        if (pendingDeletes.has(`edge:${edge.id}`)) continue

        // Remove the orphaned edge
        await db.edges.delete(edge.id)
        removed.push(edge.id)

        // Remove the target entity if the server didn't return it
        const { type: objType, id: objId } = parseNodeRef(edge.object)
        const serverIdsForType = serverIdsByType.get(objType) ?? new Set()
        if (serverIdsForType.has(objId)) continue
        if (pendingDeletes.has(`${objType}:${objId}`)) continue

        await this.deleteLocalEntity(objType, objId)
        removed.push(objId)
      }
    }

    return removed
  }

  private async deleteLocalEntity(entityType: string, entityId: string): Promise<void> {
    if (entityType === 'feature') {
      await featureStorage.deleteFeature(entityId)
      return
    }
    const tableName = ENTITY_TABLES[entityType]
    if (!tableName) return
    const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]
    await table.delete(entityId).catch(() => {})
  }

  /**
   * Revoke a top-level entity: mark it revoked and cascade-delete
   * all children reachable via edges.
   */
  private async revokeEntity(entityType: string, entityId: string): Promise<void> {
    const tableName = ENTITY_TABLES[entityType]
    if (!tableName) return
    const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]

    const entityRef = nodeRef(entityType, entityId)

    // Walk all edges from this entity and delete targets
    const edges = await edgeService.getAllFromSubject(entityRef)
    for (const edge of edges) {
      const { type: objType, id: objId } = parseNodeRef(edge.object)

      // For layer groups, recurse into their children first
      if (objType === 'group') {
        const groupRef = nodeRef('group', objId)
        const groupEdges = await edgeService.getAllFromSubject(groupRef)
        for (const ge of groupEdges) {
          const { type: childType, id: childId } = parseNodeRef(ge.object)
          await this.deleteLocalEntity(childType, childId)
        }
        await edgeService.deleteAllForNode(groupRef)
        await db.layerGroups.delete(objId)
      } else {
        await this.deleteLocalEntity(objType, objId)
      }
    }

    await edgeService.deleteAllForNode(entityRef)
    await table.update(entityId, { accessStatus: 'revoked' })
  }

  private async applyEntity(entity: EntitySummary): Promise<'added' | 'updated' | 'skipped'> {
    const tableName = ENTITY_TABLES[entity.entityType]
    if (!tableName) return 'skipped'

    const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]

    if (entity.entityType === 'teamMember') {
      const data = entity.data
      const key = [data.teamId as string, data.userId as string]
      const existing = await table.get(key)
      const localVersion =
        ((existing as Record<string, unknown> | undefined)?.version as number | undefined) ?? -1
      if (localVersion >= entity.version) return 'skipped'
      if (existing) {
        await table.update(key, { ...data, version: entity.version })
        return 'updated'
      } else {
        await table.add({ ...data, version: entity.version })
        return 'added'
      }
    }

    const existing = await table.get(entity.entityId)
    const localVersion =
      ((existing as Record<string, unknown> | undefined)?.version as number | undefined) ?? -1

    if (localVersion >= entity.version) return 'skipped'

    if (existing) {
      await table.update(entity.entityId, {
        ...entity.data,
        id: entity.entityId,
        version: entity.version,
      })
      return 'updated'
    } else {
      await table.add({ ...entity.data, id: entity.entityId, version: entity.version })
      return 'added'
    }
  }
}
